// ============================================
// R14 (t46): INVITE-LINK LIST
// ============================================
//
// Renders the existing invite links for one project in the
// project-settings page. Each row shows the token preview, the
// granted role, the creator + created-at, an expiry (if any),
// a revoked indicator, and Copy + Revoke buttons.

import { useState } from 'react';
import { Copy, Check, Link2, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/lib/i18n';
import type { ProjectRole } from '@/lib/schemas';
import {
    useProjectInviteLinks,
    useRevokeInviteLink,
    type ProjectInviteLink,
} from '@/hooks/use-invite-links';

interface InviteLinkListProps {
    projectId: string;
    onCreateClick?: () => void;
}

function statusOf(link: ProjectInviteLink): 'active' | 'revoked' | 'expired' {
    if (link.revokedAt) return 'revoked';
    if (link.expiresAt && new Date(link.expiresAt).getTime() < Date.now()) return 'expired';
    return 'active';
}

function roleLabelKey(role: ProjectRole): 'inviteLink.role.MEMBER' | 'inviteLink.role.VIEWER' {
    return role === 'MEMBER' ? 'inviteLink.role.MEMBER' : 'inviteLink.role.VIEWER';
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function InviteLinkList({ projectId, onCreateClick }: InviteLinkListProps) {
    const { t } = useI18n();
    const { toast } = useToast();
    const { data: links = [], isLoading } = useProjectInviteLinks(projectId);
    const revoke = useRevokeInviteLink(projectId);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const copyLink = async (link: ProjectInviteLink) => {
        try {
            await navigator.clipboard.writeText(link.inviteUrl);
            setCopiedId(link.id);
            toast({ title: t('inviteLink.copied') });
            setTimeout(() => setCopiedId((cur) => (cur === link.id ? null : cur)), 1500);
        } catch {
            toast({
                title: t('common.error'),
                description: t('inviteLink.copyFailed'),
                variant: 'destructive',
            });
        }
    };

    const handleRevoke = async (link: ProjectInviteLink) => {
        try {
            await revoke.mutateAsync(link.id);
            toast({ title: t('inviteLink.revoked') });
        } catch {
            toast({ title: t('common.error'), variant: 'destructive' });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-foreground-muted py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading')}
            </div>
        );
    }

    if (links.length === 0) {
        return (
            <div className="text-center py-6 text-sm text-foreground-muted">
                <Link2 className="h-6 w-6 mx-auto mb-2 opacity-50" />
                <p>{t('inviteLink.empty')}</p>
                {onCreateClick && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onCreateClick}
                        className="mt-3"
                    >
                        {t('inviteLink.createFirst')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {links.map((link) => {
                const status = statusOf(link);
                return (
                    <div
                        key={link.id}
                        className="flex flex-col gap-2 rounded-md border border-white/10 bg-surface/40 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                        <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                    variant={status === 'active' ? 'default' : 'secondary'}
                                    className="text-[10px]"
                                >
                                    {t(`inviteLink.status.${status}`)}
                                </Badge>
                                <Badge variant="outline" className="text-[10px]">
                                    {t(roleLabelKey(link.role))}
                                </Badge>
                                {link.expiresAt && (
                                    <span className="text-xs text-foreground-muted">
                                        {t('inviteLink.expiresAt').replace('{date}', formatDate(link.expiresAt))}
                                    </span>
                                )}
                            </div>
                            <div className="font-mono text-xs text-foreground-muted truncate">
                                {link.inviteUrl}
                            </div>
                            <div className="text-xs text-foreground-muted">
                                {t('inviteLink.createdBy')
                                    .replace('{name}', link.createdBy.name)
                                    .replace('{date}', formatDate(link.createdAt))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyLink(link)}
                                disabled={status !== 'active'}
                            >
                                {copiedId === link.id ? (
                                    <Check className="h-3.5 w-3.5 mr-1.5" />
                                ) : (
                                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                                )}
                                {copiedId === link.id
                                    ? t('inviteLink.copied')
                                    : t('inviteLink.copy')}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevoke(link)}
                                disabled={status !== 'active' || revoke.isPending}
                                className="text-destructive hover:text-destructive"
                            >
                                <X className="h-3.5 w-3.5 mr-1.5" />
                                {t('inviteLink.revoke')}
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
